import { useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import { ROLE_ORBITS } from "./data";

export default function RolesEcosystemSection() {
  const [selected, setSelected] = useState("association");
  const active = ROLE_ORBITS.find((r) => r.key === selected) || ROLE_ORBITS[0];
  const tabRefs = useRef({});

  const onTabKeyDown = (e, key) => {
    const keys = ROLE_ORBITS.map((r) => r.key);
    const idx = keys.indexOf(key);
    let nextIdx = null;
    if (e.key === "ArrowLeft") nextIdx = (idx + 1) % keys.length;
    else if (e.key === "ArrowRight") nextIdx = (idx - 1 + keys.length) % keys.length;
    else if (e.key === "Home") nextIdx = 0;
    else if (e.key === "End") nextIdx = keys.length - 1;
    if (nextIdx != null) {
      e.preventDefault();
      const nextKey = keys[nextIdx];
      setSelected(nextKey);
      tabRefs.current[nextKey]?.focus();
    }
  };

  const cx = 200, cy = 200, r = 150;
  const nodes = useMemo(
    () =>
      ROLE_ORBITS.map((role) => {
        const rad = (role.angle * Math.PI) / 180;
        return { ...role, x: cx + r * Math.cos(rad), y: cy + r * Math.sin(rad) };
      }),
    []
  );

  return (
    <section className="py-20 lg:py-28 bg-slate-950 text-white relative overflow-hidden" data-testid="ecosystem-section">
      <div className="absolute inset-0 opacity-40 pointer-events-none">
        <div className="absolute top-0 end-0 h-96 w-96 rounded-full bg-teal-600/20 blur-3xl" />
        <div className="absolute bottom-0 start-0 h-96 w-96 rounded-full bg-amber-500/10 blur-3xl" />
      </div>

      <div className="relative max-w-7xl mx-auto px-6 lg:px-10">
        <div className="max-w-2xl mb-14">
          <div className="text-xs font-bold uppercase tracking-widest text-amber-300">منظومة الأدوار التكاملية</div>
          <h2 className="mt-3 text-3xl sm:text-4xl lg:text-5xl font-heading font-black leading-tight">
            كل دور يلتقي عند الطالب المتدرب
          </h2>
          <p className="mt-4 text-teal-100/90 leading-relaxed">
            اختر أي دور لترى كيف تتكامل مسؤولياته مع مسار الطالب المتدرب داخل عماد.
          </p>
        </div>

        <div className="grid lg:grid-cols-12 gap-8 items-center">
          <div className="hidden md:block lg:col-span-7">
            <svg viewBox="0 0 400 400" className="w-full max-w-[520px] mx-auto" role="img" aria-label="منظومة أدوار عماد">
              <circle cx={cx} cy={cy} r={r} fill="none" stroke="rgba(255,255,255,0.08)" strokeDasharray="4 6" />
              {nodes.map((n) => (
                <line
                  key={`ln-${n.key}`}
                  x1={cx} y1={cy} x2={n.x} y2={n.y}
                  stroke={n.key === selected ? "#fbbf24" : "rgba(255,255,255,0.12)"}
                  strokeWidth={n.key === selected ? 2.5 : 1}
                  strokeLinecap="round"
                />
              ))}
              <g>
                <circle cx={cx} cy={cy} r={48} fill="#0f766e" stroke="#f59e0b" strokeWidth={2} />
                <text x={cx} y={cy - 4} textAnchor="middle" fontSize="11" fill="#fef3c7" fontWeight="700">الطالب</text>
                <text x={cx} y={cy + 10} textAnchor="middle" fontSize="11" fill="#fef3c7" fontWeight="700">المتدرب</text>
                <text x={cx} y={cy + 24} textAnchor="middle" fontSize="8" fill="rgba(254,243,199,0.7)">مركز الرحلة</text>
              </g>
              {nodes.map((n) => {
                const isSel = n.key === selected;
                return (
                  <g key={n.key} className="cursor-pointer" onClick={() => setSelected(n.key)}>
                    <circle cx={n.x} cy={n.y} r={isSel ? 34 : 28} fill={isSel ? "#f59e0b" : "rgba(255,255,255,0.08)"} stroke={isSel ? "#fef3c7" : "rgba(255,255,255,0.25)"} strokeWidth={isSel ? 2 : 1} />
                    <foreignObject x={n.x - 16} y={n.y - 16} width={32} height={32}>
                      <div className={`h-8 w-8 grid place-items-center ${isSel ? "text-slate-900" : "text-white/80"}`}>
                        <n.icon size={18} strokeWidth={1.75} />
                      </div>
                    </foreignObject>
                    <text x={n.x} y={n.y + 50} textAnchor="middle" fontSize="11" fill={isSel ? "#fef3c7" : "rgba(255,255,255,0.7)"} fontWeight={isSel ? "700" : "500"}>
                      {n.label}
                    </text>
                  </g>
                );
              })}
            </svg>
            <div className="mt-6 flex flex-wrap gap-2 justify-center" role="tablist" aria-label="أدوار المنظومة">
              {ROLE_ORBITS.map((r) => (
                <button
                  key={r.key}
                  ref={(el) => (tabRefs.current[r.key] = el)}
                  role="tab"
                  aria-selected={r.key === selected}
                  tabIndex={r.key === selected ? 0 : -1}
                  onClick={() => setSelected(r.key)}
                  onKeyDown={(e) => onTabKeyDown(e, r.key)}
                  className={`px-4 py-1.5 rounded-full text-xs font-semibold border transition-all focus:outline-none focus-visible:ring-2 focus-visible:ring-amber-300/60 ${
                    r.key === selected
                      ? "bg-amber-400 text-slate-900 border-amber-300"
                      : "bg-white/5 text-white/80 border-white/15 hover:bg-white/10"
                  }`}
                  data-testid={`ecosystem-role-${r.key}`}
                >
                  {r.label}
                </button>
              ))}
            </div>
          </div>

          <div className="md:hidden">
            <div className="relative flex flex-col items-center gap-4">
              <div className="h-16 w-16 rounded-full bg-teal-600 border-2 border-amber-400 grid place-items-center text-white font-bold text-[11px] text-center leading-tight px-1">الطالب<br/>المتدرب</div>
              {ROLE_ORBITS.map((r) => {
                const isSel = r.key === selected;
                return (
                  <button
                    key={r.key}
                    onClick={() => setSelected(r.key)}
                    aria-pressed={isSel}
                    className={`w-full max-w-sm p-4 rounded-2xl border text-start flex items-center gap-3 transition-all ${
                      isSel
                        ? "bg-amber-400 text-slate-900 border-amber-300"
                        : "bg-white/5 text-white/85 border-white/15"
                    }`}
                    data-testid={`ecosystem-role-mobile-${r.key}`}
                  >
                    <r.icon className="h-5 w-5" strokeWidth={1.75} />
                    <span className="font-heading font-bold text-sm">{r.label}</span>
                  </button>
                );
              })}
            </div>
          </div>

          <aside className="lg:col-span-5" aria-live="polite">
            <div className="p-4 lg:p-8 rounded-3xl bg-white/5 backdrop-blur-sm border border-white/10" data-testid="ecosystem-detail">
              <div className="inline-flex items-center gap-2 text-xs font-bold uppercase tracking-widest text-amber-300 mb-3">
                <active.icon className="h-4 w-4" /> {active.label}
              </div>
              <p className="text-lg leading-relaxed">{active.value}</p>
              <div className="mt-6 pt-6 border-t border-white/10 flex items-center justify-between text-sm">
                <span className="text-white/60">هذه العلاقة داخل رحلة الطالب المتدرب</span>
                <Link
                  to={`/partners?role=${active.key}`}
                  className="inline-flex items-center gap-1.5 text-amber-300 font-semibold hover:gap-2.5 transition-all"
                  data-testid="ecosystem-partner-link"
                >
                  انضم بهذا الدور <ArrowLeft className="h-4 w-4" />
                </Link>
              </div>
            </div>
          </aside>
        </div>
      </div>
    </section>
  );
}
